/**
 * TemplatesView — routes `?template` to either the campaign's template
 * library or the editor page for one row (new / edit / fork).
 *
 * Two BE rules shape the write paths:
 *  - Global rows are read-only (TEMPLATE_GLOBAL_READONLY): no edit, delete or
 *    rename here. They open in a read-only view, and can be forked into the
 *    campaign. A viewer without outreach:write gets the same view for any row.
 *  - A brand row is shared, so editing or archiving one answers 409
 *    TEMPLATE_USED_BY with the count until `confirm_brand=true` is sent. The
 *    count comes from that error, so the confirmation is raised in response to
 *    it rather than guessed at up front.
 */
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useCampaignDetail, useOutreachRows, useOutreachTemplateMutations, useOutreachTemplates } from '@discovr/features';
import type {
  OutreachTemplate,
  OutreachTemplateGroups,
  OutreachTemplateInput,
  OutreachTemplatePatch,
} from '@discovr/types';
import { ActionConfirmDialog } from '../../../components/common/ActionConfirmDialog';
import { Skeleton } from '../../../components/ui/skeleton';
import { useOutreachSubParam } from '../../useOutreachSubParam';
import { TemplateEditor, type TemplateEditorMode } from './TemplateEditor';
import { TemplateLibrary } from './TemplateLibrary';
import { apiErrorCopy } from './sendLabels';

/** The preview picker lists the first page; the subject estimate scans the whole page for the longest creator name. */
const PREVIEW_TARGET_LIMIT = 25;
const NAME_SCAN_LIMIT = 100;

export interface TemplatesViewProps {
  campaignId: string;
  canWrite: boolean;
}

interface EditorTarget {
  mode: TemplateEditorMode;
  initial?: OutreachTemplate;
}

type BrandConfirm =
  | { kind: 'save'; templateId: number; patch: OutreachTemplatePatch; usedBy: number }
  | { kind: 'archive'; templateId: number; usedBy: number }
  | { kind: 'restore'; templateId: number; usedBy: number };

/** The used-by count the BE puts on its 409, or null when this isn't that error. */
function usedByFromError(err: unknown): number | null {
  if (typeof err !== 'object' || err === null) return null;
  const shaped = err as { code?: unknown; details?: unknown };
  if (shaped.code !== 'TEMPLATE_USED_BY') return null;
  const details = shaped.details as { used_by?: unknown; usedBy?: unknown } | undefined;
  const raw = details?.used_by ?? details?.usedBy;
  return typeof raw === 'number' ? raw : 0;
}

const plural = (n: number, noun: string) => `${n} ${noun}${n === 1 ? '' : 's'}`;

/** True only because new first touches keep sending from the sequence
 *  snapshot frozen before the archive (Task 48, Go half). */
function usageCopy(usedBy: number): string {
  if (usedBy === 0) return 'Not used by any campaign.';
  return `Used by ${plural(usedBy, 'campaign')} — ${usedBy === 1 ? 'it keeps its' : 'they keep their'} current sequence.`;
}

const errorMessage = (err: unknown, fallback: string) => apiErrorCopy(err, fallback, 'template');

function ActionErrorBanner({ message }: { message: string }) {
  return (
    <p role="alert" className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-caption-regular text-rose-600">
      {message}
    </p>
  );
}

function findTemplate(groups: OutreachTemplateGroups, id: number, justSaved: OutreachTemplate | null): OutreachTemplate | undefined {
  if (justSaved?.id === id) return justSaved;
  return [...groups.global, ...groups.brand, ...groups.campaign].find((t) => t.id === id);
}

/** Resolves the `?template` param against the loaded groups (plus the row a
 *  save just returned, which the list may not hold yet), or null while
 *  there's nothing to resolve yet (still loading, or no param). */
function resolveTarget(
  param: string | null,
  groups: OutreachTemplateGroups,
  justSaved: OutreachTemplate | null,
  canWrite: boolean,
): EditorTarget | null | undefined {
  if (param === null) return null;
  if (param === 'new') return { mode: 'create' };
  if (param.startsWith('fork:')) {
    const source = findTemplate(groups, Number(param.slice('fork:'.length)), justSaved);
    return source ? { mode: 'fork', initial: source } : undefined;
  }
  const source = findTemplate(groups, Number(param), justSaved);
  if (!source) return undefined;
  return source.scope === 'global' || source.archivedAt || !canWrite
    ? { mode: 'view', initial: source }
    : { mode: 'edit', initial: source };
}

export function TemplatesView({ campaignId, canWrite }: TemplatesViewProps) {
  const templatesQuery = useOutreachTemplates(campaignId);
  const { create, patch, archive, restore, preview, fillStep } = useOutreachTemplateMutations(campaignId);
  const rowsQuery = useOutreachRows(campaignId, { tab: 'all', page: 1, limit: NAME_SCAN_LIMIT });
  const { template: param, setTemplate } = useOutreachSubParam();

  const [archiveTarget, setArchiveTarget] = useState<OutreachTemplate | null>(null);
  const [brandConfirm, setBrandConfirm] = useState<BrandConfirm | null>(null);
  // Failures raised from inside a confirm dialog, which has no error slot of
  // its own — surfaced as a banner rather than swallowed.
  const [actionError, setActionError] = useState<string | null>(null);
  // The save response is the only carrier of lint warnings (the list route
  // never has them), so a save that warns keeps the editor open on it.
  const [justSaved, setJustSaved] = useState<OutreachTemplate | null>(null);
  const openTemplate = (next: string | number | null) => {
    setActionError(null);
    setJustSaved(null);
    setTemplate(next);
  };
  const afterSave = (saved: OutreachTemplate) => {
    if (saved.warnings && saved.warnings.length > 0) {
      setJustSaved(saved);
      setTemplate(saved.id);
      return;
    }
    openTemplate(null);
  };

  const campaign = useCampaignDetail(campaignId, { rosterEnabled: false }).campaign.data;
  const rows = rowsQuery.data?.rows ?? [];
  const entries = rows.slice(0, PREVIEW_TARGET_LIMIT).map((row) => ({ entryId: row.entryId, username: row.username }));
  const subjectContext = {
    campaignName: campaign?.name,
    brandName: campaign?.brand_name,
    creatorNames: rows.map((row) => row.displayName?.trim() || `@${row.username}`),
  };
  const groups: OutreachTemplateGroups = templatesQuery.data ?? { global: [], brand: [], campaign: [] };

  // `undefined` means the param names a row that isn't in the loaded groups
  // (deleted elsewhere, or a stale link) — bounce back to the library.
  const target = resolveTarget(param, groups, justSaved, canWrite);
  const isGone = target === undefined && !templatesQuery.isLoading;

  useEffect(() => {
    if (!isGone) return;
    toast.error('That template is gone.', { id: 'template-bounce' });
    setTemplate(null);
  }, [isGone, setTemplate]);

  const handlePreview = (templateId: number, entryId: string) => preview.mutateAsync({ templateId, entryId });

  /** The persisted row, or null when a confirm-brand dialog is now up and
   *  the editor should stay open. */
  const save = async (
    mode: TemplateEditorMode,
    initial: OutreachTemplate | undefined,
    input: OutreachTemplateInput | OutreachTemplatePatch,
    confirmBrand: boolean,
  ): Promise<OutreachTemplate | null> => {
    setActionError(null);
    if (mode !== 'edit' || !initial) {
      return create.mutateAsync(input as OutreachTemplateInput);
    }
    const templateId = initial.id;
    const templatePatch = input as OutreachTemplatePatch;
    try {
      return await patch.mutateAsync({ templateId, patch: templatePatch, confirmBrand });
    } catch (err) {
      const usedBy = usedByFromError(err);
      if (usedBy === null) throw err;
      setBrandConfirm({ kind: 'save', templateId, patch: templatePatch, usedBy });
      return null;
    }
  };

  const runArchive = async (templateId: number, confirmBrand: boolean) => {
    setActionError(null);
    try {
      await archive.mutateAsync({ templateId, confirmBrand });
      setArchiveTarget(null);
      setBrandConfirm(null);
    } catch (err) {
      const usedBy = usedByFromError(err);
      setArchiveTarget(null);
      if (usedBy === null) {
        setActionError(errorMessage(err, 'Could not archive this template.'));
        return;
      }
      setBrandConfirm({ kind: 'archive', templateId, usedBy });
    }
  };

  const runRestore = async (templateId: number) => {
    setActionError(null);
    try {
      await restore.mutateAsync({ templateId });
    } catch (err) {
      const usedBy = usedByFromError(err);
      if (usedBy === null) {
        setActionError(errorMessage(err, 'Could not restore this template.'));
        return;
      }
      setBrandConfirm({ kind: 'restore', templateId, usedBy });
    }
  };

  const confirmBrandAction = async () => {
    if (!brandConfirm) return;
    setActionError(null);
    try {
      if (brandConfirm.kind === 'archive') {
        await archive.mutateAsync({ templateId: brandConfirm.templateId, confirmBrand: true });
      } else if (brandConfirm.kind === 'restore') {
        await restore.mutateAsync({ templateId: brandConfirm.templateId, confirmBrand: true });
      } else {
        const saved = await patch.mutateAsync({
          templateId: brandConfirm.templateId,
          patch: brandConfirm.patch,
          confirmBrand: true,
        });
        afterSave(saved);
      }
      setBrandConfirm(null);
    } catch (err) {
      setBrandConfirm(null);
      setActionError(errorMessage(err, 'Could not apply this change to every campaign.'));
    }
  };

  const brandConfirmCopy =
    brandConfirm?.kind === 'archive'
      ? {
          title: 'Archive for all campaigns?',
          description:
            brandConfirm.usedBy === 0
              ? 'This is a brand template shared by every campaign of this brand. No campaign uses it right now.'
              : `${usageCopy(brandConfirm.usedBy)} Archive for all of them?`,
          confirmLabel: 'Archive for all',
          variant: 'delete' as const,
        }
      : brandConfirm?.kind === 'restore'
        ? {
            title: 'Restore for all campaigns?',
            description: 'This is a brand template shared by every campaign of this brand. Restoring it puts it back in the picker for all of them.',
            confirmLabel: 'Restore for all',
            variant: 'change' as const,
          }
        : {
            title: 'Save for all campaigns?',
            description: `This brand template is used by ${plural(brandConfirm?.usedBy ?? 0, 'campaign')}. Save for all of them?`,
            confirmLabel: 'Save for all',
            variant: 'change' as const,
          };

  if (target) {
    const { mode, initial } = target;
    return (
      <>
        {actionError ? <ActionErrorBanner message={actionError} /> : null}
        <TemplateEditor
          key={`${mode}-${initial?.id ?? 'new'}`}
          mode={mode}
          initial={initial}
          scopeOptions={mode === 'create' || mode === 'fork' ? ['campaign', 'brand'] : []}
          kindOptions={mode === 'create' || mode === 'fork' ? ['sequence', 'after_reply', 'other'] : undefined}
          onDuplicate={canWrite && initial ? () => openTemplate(`fork:${initial.id}`) : undefined}
          entries={entries}
          subjectContext={subjectContext}
          onPreview={handlePreview}
          onFillStep={canWrite ? (req) => fillStep.mutateAsync(req) : undefined}
          lintWarnings={justSaved && initial?.id === justSaved.id ? justSaved.warnings : undefined}
          onSave={async (input, confirmBrand) => {
            const saved = await save(mode, initial, input, confirmBrand);
            if (saved) afterSave(saved);
          }}
          onCancel={() => openTemplate(null)}
        />
        <ActionConfirmDialog
          open={!!brandConfirm}
          onOpenChange={(next) => {
            if (!next) setBrandConfirm(null);
          }}
          variant={brandConfirmCopy.variant}
          title={brandConfirmCopy.title}
          description={brandConfirmCopy.description}
          confirmLabel={brandConfirmCopy.confirmLabel}
          busy={patch.isPending || archive.isPending || restore.isPending}
          onConfirm={() => confirmBrandAction()}
        />
      </>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-5">
      {actionError ? <ActionErrorBanner message={actionError} /> : null}

      {templatesQuery.isError ? (
        <p className="py-16 text-center text-sm text-rose-500">Couldn&apos;t load templates. Please refresh.</p>
      ) : templatesQuery.isLoading ? (
        <div className="flex flex-col gap-3">
          <Skeleton className="h-16 w-full rounded-xl" />
          <Skeleton className="h-16 w-full rounded-xl" />
        </div>
      ) : (
        <TemplateLibrary
          groups={groups}
          canWrite={canWrite}
          onNew={() => openTemplate('new')}
          onOpen={(t) => openTemplate(t.id)}
          onEdit={(t) => openTemplate(t.id)}
          onFork={(t) => openTemplate(`fork:${t.id}`)}
          onArchive={(t) => {
            setActionError(null);
            setArchiveTarget(t);
          }}
          onRestore={(t) => runRestore(t.id)}
        />
      )}

      <ActionConfirmDialog
        open={!!archiveTarget}
        onOpenChange={(next) => {
          if (!next) setArchiveTarget(null);
        }}
        variant="delete"
        title="Archive template?"
        description={
          archiveTarget
            ? `"${archiveTarget.name}" is archived, not deleted, and stops appearing in the picker. ${usageCopy(archiveTarget.usedBy)}`
            : undefined
        }
        confirmLabel="Archive"
        busy={archive.isPending}
        onConfirm={() => (archiveTarget ? runArchive(archiveTarget.id, false) : undefined)}
      />

      <ActionConfirmDialog
        open={!!brandConfirm}
        onOpenChange={(next) => {
          if (!next) setBrandConfirm(null);
        }}
        variant={brandConfirmCopy.variant}
        title={brandConfirmCopy.title}
        description={brandConfirmCopy.description}
        confirmLabel={brandConfirmCopy.confirmLabel}
        busy={archive.isPending || restore.isPending || patch.isPending}
        onConfirm={() => confirmBrandAction()}
      />
    </div>
  );
}
